/**
 * @fileoverview Google Ads S2S Conversion Tracking Contracts
 * @module @monorepo/types/google-ads
 *
 * Zod schemas for Google Ads API integration.
 * Contract-first design following project standards.
 *
 * @see https://developers.google.com/google-ads/api/docs/conversions/upload-clicks
 */

import { z } from 'zod';

/**
 * Google Ads Configuration Schema
 * All fields loaded from environment variables
 */
export const GoogleAdsConfigSchema = z.object({
	/** Google Ads Developer Token */
	developerToken: z.string().min(1, 'Developer token is required'),
	/** OAuth2 Client ID */
	clientId: z.string().min(1, 'Client ID is required'),
	/** OAuth2 Client Secret */
	clientSecret: z.string().min(1, 'Client secret is required'),
	/** OAuth2 Refresh Token */
	refreshToken: z.string().min(1, 'Refresh token is required'),
	/** Google Ads Customer ID (without hyphens) */
	customerId: z
		.string()
		.min(1, 'Customer ID is required')
		.regex(/^\d+$/, 'Customer ID must be numeric (no hyphens)'),
	/** Conversion Action ID for the specific conversion type */
	conversionActionId: z.string().min(1, 'Conversion action ID is required'),
	/** Login Customer ID (for MCC accounts, optional) */
	loginCustomerId: z
		.string()
		.regex(/^\d+$/, 'Login customer ID must be numeric')
		.optional(),
});

export type GoogleAdsConfig = z.infer<typeof GoogleAdsConfigSchema>;

/**
 * Click Conversion Upload Payload Schema
 * Based on Google Ads API ClickConversion resource
 *
 * @see https://developers.google.com/google-ads/api/reference/rpc/latest/ClickConversion
 */
export const ConversionUploadSchema = z.object({
	/** Google Click ID (GCLID) - Required for click conversions */
	gclid: z.string().min(1, 'GCLID is required'),
	/**
	 * Conversion date/time in Google Ads format
	 * Format: yyyy-mm-dd hh:mm:ss+|-hh:mm
	 * Example: 2026-02-04 23:30:00+01:00
	 */
	conversionDateTime: z.string().min(1, 'Conversion datetime is required'),
	/** Conversion value in account currency (optional) */
	conversionValue: z.number().nonnegative().optional(),
	/** ISO 4217 currency code (e.g., 'EUR', 'USD') */
	currencyCode: z
		.string()
		.length(3, 'Currency code must be 3 characters')
		.toUpperCase()
		.optional()
		.default('EUR'),
	/** Order ID for deduplication (optional but recommended) */
	orderId: z.string().optional(),
});

export type ConversionUpload = z.infer<typeof ConversionUploadSchema>;

/**
 * Conversion Upload Result Schema
 */
export const ConversionUploadResultSchema = z.object({
	/** Whether the upload was successful */
	success: z.boolean(),
	/** The GCLID that was uploaded */
	gclid: z.string(),
	/** Error message if failed */
	error: z.string().optional(),
	/** Partial failure error from Google Ads API */
	partialFailureError: z.string().optional(),
});

export type ConversionUploadResult = z.infer<typeof ConversionUploadResultSchema>;

/**
 * Google Ads Service Status
 */
export const GoogleAdsStatusSchema = z.object({
	/** Whether the service is enabled and configured */
	enabled: z.boolean(),
	/** Customer ID if configured */
	customerId: z.string().optional(),
	/** Error message if not enabled */
	error: z.string().optional(),
});

export type GoogleAdsStatus = z.infer<typeof GoogleAdsStatusSchema>;
